import { Link, useParams } from "react-router-dom";
import type { MatterDetailRow } from "@themislex/shared";
import { useApi } from "../../api/hooks";
import { InvoiceTable } from "../../components/InvoiceTable";
import { MatterInfoCard } from "../../components/MatterInfoCard";
import { Card, ErrorNote, PageHeader, Spinner } from "../../components/ui";

export default function MatterDetail() {
  const { id = "" } = useParams();
  const { data, error, loading } = useApi<{ matter: MatterDetailRow }>(
    `/api/matters/${id}`,
  );

  if (loading) {
    return (
      <div className="flex justify-center py-16 text-muted">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (error || !data) {
    return <ErrorNote>{error ?? "Matter not found."}</ErrorNote>;
  }

  const matter = data.matter;
  const outstanding = matter.invoices.reduce(
    (sum, invoice) =>
      invoice.displayStatus === "paid" || invoice.displayStatus === "draft"
        ? sum
        : sum + invoice.outstandingMinor,
    0,
  );

  return (
    <>
      <PageHeader title="Matter" subtitle={`Opened ${matter.createdAt.slice(0, 10)}`} />

      <MatterInfoCard
        matter={matter}
        actions={
          <Link
            to={`/desk/matters/${matter.id}/edit`}
            className="btn btn-secondary no-underline"
          >
            Edit matter
          </Link>
        }
      />

      <div className="mt-8 space-y-8">
        <Card
          title="Invoices"
          actions={
            <Link
              to={`/desk/invoices/new?matterId=${matter.id}&clientId=${matter.clientId}`}
              className="btn btn-primary px-3 py-1.5 text-xs no-underline"
            >
              New invoice for this matter
            </Link>
          }
        >
          {matter.invoices.length > 0 && outstanding > 0 && (
            <p className="mb-4 rounded-md px-4 py-2 text-sm" style={{ backgroundColor: "rgb(var(--tl-warning-bg))", color: "rgb(var(--tl-warning-fg))" }}>
              Pending against this matter: ₹{(outstanding / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </p>
          )}
          <InvoiceTable
            invoices={matter.invoices}
            lawyerView
            emptyHint="No invoices raised for this matter yet."
          />
        </Card>
      </div>
    </>
  );
}
